import { useEffect, useState } from "react"
import { Modal } from "common/Modal"
import UserService from "services/user.service"
import { useNotifications } from "hooks/useNotification"
import { validateAmount } from "./utils"

const service = new UserService()
const INPUT_CLASS = "w-full px-4 py-2 font-light text-gray-800 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none focus:ring-0"

type EditForm = {
    amount: string
    vendorName: string
    city: string
    transactionDate: string
}

type Props = {
    showModal: boolean
    setShowModal: (value: boolean) => void
    operationId: string
    expense: any
    onSuccess: () => void
}

const initialForm: EditForm = { amount: "", vendorName: "", city: "", transactionDate: "" }

export const EditExpenseModal = ({ showModal, setShowModal, operationId, expense, onSuccess }: Props) => {
    const [form, setForm] = useState<EditForm>(initialForm)
    const [saving, setSaving] = useState(false)
    const { successMessage, errorMessage } = useNotifications()

    useEffect(() => {
        if (showModal && expense) {
            setForm({
                amount: expense.amount || "",
                vendorName: expense.vendorName || "",
                city: expense.city || "",
                transactionDate: expense.transactionDate
                    ? new Date(expense.transactionDate).toISOString().split("T")[0]
                    : "",
            })
        }
    }, [showModal, expense])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const err = validateAmount(form.amount)
        if (err) {
            errorMessage(err)
            return
        }
        setSaving(true)
        try {
            await service.updateOperationExpense({
                operationId,
                expenseId: expense._id,
                amount: form.amount,
                vendorName: form.vendorName,
                city: form.city,
                transactionDate: form.transactionDate,
            })
            successMessage("Expense updated successfully")
            setForm(initialForm)
            setShowModal(false)
            onSuccess()
        } catch (error: any) {
            errorMessage(error?.response?.data?.message || "Failed to update expense")
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        setShowModal(false)
        setForm(initialForm)
    }

    return (
        <Modal
            showModal={showModal}
            setShowModal={setShowModal}
            title="Edit Expense"
            width="w-full md:w-1/2"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-1.5">
                    <label className="field-label">
                        Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.amount}
                        onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                        required
                        className={INPUT_CLASS}
                        placeholder="Enter amount"
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="field-label">Vendor Name</label>
                    <input
                        type="text"
                        value={form.vendorName}
                        onChange={(e) => setForm((prev) => ({ ...prev, vendorName: e.target.value }))}
                        className={INPUT_CLASS}
                        placeholder="Enter vendor name"
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="field-label">City</label>
                    <input
                        type="text"
                        value={form.city}
                        onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
                        className={INPUT_CLASS}
                        placeholder="Enter city"
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="field-label">Transaction Date</label>
                    <input
                        type="date"
                        value={form.transactionDate}
                        onChange={(e) => setForm((prev) => ({ ...prev, transactionDate: e.target.value }))}
                        className={INPUT_CLASS}
                    />
                </div>

                <div className="flex gap-3 pt-4">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? "Updating..." : "Update Expense"}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
